import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Mention } from '@tiptap/extension-mention';
import { common, createLowlight } from 'lowlight';
import type { JSONContent } from '@tiptap/react';
import { EditorToolbar } from './EditorToolbar';
import { mentionSuggestion } from './MentionExtension';
import { tagSuggestion } from './TagExtension';
import { entityLinkSuggestion } from './EntityLinkExtension';
import { TaskReference } from './TaskReferenceExtension';
import { EntityLinkNode } from './EntityLinkNode';
import { ImageUpload } from './ImageUploadExtension';
import Details from '@tiptap/extension-details';
import './editor-styles.css';

const lowlight = createLowlight(common);

/**
 * Extended Mention node that adds a `color` attribute for tag mentions.
 * The stock Mention extension only has id, label, and mentionSuggestionChar.
 */
const DossierMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => {
          if (!attributes.color) return {};
          return { 'data-color': attributes.color };
        },
      },
    };
  },
});

interface DossierEditorProps {
  content: JSONContent | null;
  onUpdate: (content: JSONContent) => void;
  editable?: boolean;
  placeholder?: string;
}

export function DossierEditor({
  content,
  onUpdate,
  editable = true,
  placeholder = 'Start writing...',
}: DossierEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable the built-in codeBlock so we use lowlight version
        codeBlock: false,
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Highlight.configure({
        multicolor: false,
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      DossierMention.configure({
        HTMLAttributes: {
          class: 'mention-node',
        },
        renderHTML({ node }) {
          const char = node.attrs.mentionSuggestionChar || '@';
          const label = node.attrs.label ?? node.attrs.id;
          const isTag = char === '#';

          const attrs: Record<string, string> = {
            'data-type': 'mention',
            'data-id': node.attrs.id,
            'data-label': label,
            'data-mention-suggestion-char': char,
            class: isTag ? 'mention-node mention-tag' : 'mention-node mention-person',
          };

          // For tags, store the color as a data attribute and inline style
          if (isTag && node.attrs.color) {
            attrs['data-color'] = node.attrs.color;
            attrs.style = `--tag-color: ${node.attrs.color}`;
          }

          return ['span', attrs, `${char}${label}`];
        },
        renderText({ node }) {
          const char = node.attrs.mentionSuggestionChar || '@';
          return `${char}${node.attrs.label ?? node.attrs.id}`;
        },
        deleteTriggerWithBackspace: true,
        suggestions: [mentionSuggestion, tagSuggestion],
      }),
      TaskReference,
      EntityLinkNode.configure({
        suggestion: entityLinkSuggestion,
      }),
      ImageUpload,
      Details.configure({
        persist: true,
        HTMLAttributes: {
          class: 'dossier-details',
        },
      }),
    ],
    content: content ?? undefined,
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
  });

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-bg-card">
      {editable && <EditorToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}
